import React from "react";
import { currentDateTime } from "../../utils/utils";
import "./TaxInvoiceB.scss";

// ─── Types ────────────────────────────────────────────────────────────────────
interface TaxInvoiceProps {
  title: string;
  invoiceRef?: React.RefObject<HTMLDivElement>;
  businessInfo: any;
  userInfo: any;
  customerInfo: any;
  billDetails: any;
  amountDetails: any;
  paymentType: any;
  toWords: any;
}
// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number, decimals = 2): string =>
  n.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

// ─── Component ────────────────────────────────────────────────────────────────

const TaxInvoiceB: React.FC<TaxInvoiceProps> = ({
  title,
  invoiceRef,
  businessInfo,
  userInfo,
  customerInfo,
  billDetails,
  amountDetails,
  paymentType,
  toWords,
}) => {
  const calculateDiscountPercentage = (discount: number, subTotal: number) => {
    return (discount / (subTotal + discount)) * 100;
  };

  return (
    <>
      <div className="inv-root" ref={invoiceRef}>
        <div className="inv-no-print">
          {Number(userInfo?.bill_print_count) > 0 ? (
            <>
              <div className="inv-copy-label">
                Copy of Original ({Number(userInfo?.bill_print_count)})
              </div>
            </>
          ) : (
            ""
          )}
        </div>
        {/* ══ HEADER ══ */}
        <div className="inv-header">
          <div className="inv-co-name">{businessInfo?.name}</div>
          <div className="inv-co-sub">{businessInfo?.address}</div>
          <div>
            <span className="inv-vat-badge">VAT No : {businessInfo?.pan}</span>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <span className="inv-title-badge">{title}</span>
        </div>

        {/* ══ META ══ */}
        <div className="row g-2 mb-2">
          <div className="col-7">
            <div className="inv-meta-box h-100">
              {(
                [
                  ["Invoice No", userInfo?.document_no],
                  ["Customer Name", customerInfo?.name],
                  ["Address", customerInfo?.additional_info?.address],
                  ["Customer Phone", customerInfo?.mobile_no],
                  ["Pan No", customerInfo?.pan],
                ] as [string, string][]
              ).map(([label, value]) => (
                <div
                  key={label}
                  style={{ display: "flex", gap: 4, paddingBottom: 2 }}
                >
                  <span className="inv-meta-label">{label}</span>
                  <span className="inv-meta-value">: {value}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="col-5">
            <div className="inv-meta-box h-100">
              {(
                [
                  ["Invoice Date", userInfo?.bill_miti_ad],
                  ["Invoice Miti", userInfo?.bill_miti],
                  [
                    "Payment Mode",
                    [
                      paymentType?.cash && "Cash",
                      paymentType?.bank && "Bank",
                      paymentType?.credit && "Credit",
                    ]
                      .filter(Boolean)
                      .join("/") || "-",
                  ],
                ] as [string, string][]
              ).map(([label, value]) => (
                <div
                  key={label}
                  style={{ display: "flex", gap: 4, paddingBottom: 2 }}
                >
                  <span className="inv-meta-label">{label}</span>
                  <span className="inv-meta-value">: {value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ ITEMS TABLE ══ */}
        <table className="inv-table">
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: 38 }} />
            <col />
            <col style={{ width: 38 }} />
            <col style={{ width: 26 }} />
            <col style={{ width: 38 }} />
            <col style={{ width: 26 }} />
            <col style={{ width: 42 }} />
            <col style={{ width: 32 }} />
            <col style={{ width: 50 }} />
            <col style={{ width: 54 }} />
          </colgroup>
          <thead>
            <tr>
              {(
                [
                  ["SNo", "center"],
                  ["HSC", "center"],
                  ["Product Name", "left"],
                  ["Alt Qty", "right"],
                  ["Unit", "center"],
                  ["Qty", "right"],
                  ["Unit", "center"],
                  ["Rate", "right"],
                  ["PD%", "right"],
                  ["P.Dis", "right"],
                  ["Amount", "right"],
                ] as [string, string][]
              ).map(([h, align], i) => (
                <th
                  key={i}
                  style={{
                    textAlign: align as React.CSSProperties["textAlign"],
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {billDetails?.map((item: any, idx: number) => (
              <tr key={idx}>
                <td style={{ textAlign: "center" }}>{idx + 1}</td>
                <td style={{ textAlign: "center" }}>-</td>
                <td>{item.product}</td>
                <td style={{ textAlign: "right" }}>
                  {item.secondary_unit
                    ? fmt(item.secondary_unit / item.relation)
                    : fmt(item.primary_unit)}
                </td>
                <td style={{ textAlign: "center" }}>
                  {item.primary_unit_measure}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.secondary_unit
                    ? fmt(item.secondary_unit)
                    : fmt(item.primary_unit * item.relation)}
                </td>
                <td style={{ textAlign: "center" }}>
                  {item.secondary_unit_measure}
                </td>
                <td style={{ textAlign: "right" }}>
                  {fmt(item.secondary_price)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {userInfo?.is_total_discount
                    ? fmt(0)
                    : fmt(
                        calculateDiscountPercentage(
                          item.discount,
                          item.sub_total,
                        ),
                      )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {userInfo?.is_total_discount ? fmt(0) : fmt(item.discount)}
                </td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {fmt(item.sub_total)}
                </td>
              </tr>
            ))}
            {/* Fill remaining rows to maintain consistent table height */}
            {billDetails &&
              Array.from(
                { length: Math.max(0, 20 - (billDetails?.length || 0)) },
                (_, idx) => (
                  <tr key={`empty-${idx}`}>
                    <td colSpan={11}>{"\u00a0"}</td>
                  </tr>
                ),
              )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={10} style={{ textAlign: "right" }}>
                TOTAL
              </td>
              <td style={{ textAlign: "right" }}>
                {fmt(amountDetails?.sub_total)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* ══ FOOTER ══ */}
        <div className="row g-3">
          <div className="col-7 d-flex flex-column justify-content-between">
            <div style={{ fontSize: 10, color: "#555", marginBottom: 4 }}>
              Printing Date &amp; Time : {String(currentDateTime())}
            </div>
            <div style={{ fontSize: 12, fontStyle: "italic", color: "#333" }}>
              <span style={{ fontStyle: "normal", color: "#555" }}>
                In Words :{" "}
              </span>
              {toWords.convert(amountDetails?.total_amount)}
            </div>

            <div style={{ fontSize: 10, marginTop: 6 }}>
              <span style={{ color: "#555" }}>Remarks : </span>
              <strong>{userInfo?.customer_notes || `Thank You`}</strong>
            </div>
          </div>

          <div className="col-5">
            <table className="inv-summary-table">
              <tbody>
                <tr>
                  <td className="inv-sum-label">Gross Amount</td>
                  <td className="inv-sum-value">
                    {fmt(amountDetails?.sub_total)}
                  </td>
                </tr>
                <tr>
                  <td className="inv-sum-label">
                    Discount{" "}
                    {fmt(
                      calculateDiscountPercentage(
                        amountDetails?.discount_paid,
                        amountDetails?.sub_total,
                      ),
                    )}
                    %
                  </td>
                  <td className="inv-sum-value">
                    {fmt(amountDetails?.discount_paid)}
                  </td>
                </tr>

                <tr className="inv-sum-sep">
                  <td style={{ fontWeight: 700, padding: "3.5px 9px" }}>
                    Taxable Amount
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: 700,
                      padding: "3.5px 9px",
                    }}
                  >
                    {fmt(amountDetails?.taxable_amount)}
                  </td>
                </tr>
                <tr>
                  <td className="inv-sum-label">VAT 13.00%</td>
                  <td className="inv-sum-value">
                    {fmt(amountDetails?.tax_received)}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="inv-net-row">
                  <td>Net Amount</td>
                  <td style={{ textAlign: "right" }}>
                    {fmt(amountDetails?.total_amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* ══ DISCLAIMER ══ */}
        <div className="inv-disclaimer">
          Please check the goods at the time of delivery, otherwise we will not
          be responsible for any claim.
        </div>

        {/* ══ SIGNATURES ══ */}
        <div className="row mt-5 pt-2">
          <div className="col-4 text-center">
            <div className="inv-sig-name">{userInfo?.name}</div>
            <div className="inv-sig-role">Prepared By</div>
          </div>
          <div className="col-4" />
          <div className="col-4 text-center inv-sig-container">
            <div className="inv-sig-name">Authorized Signatory</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TaxInvoiceB;
